import { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faBoxOpen,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../../style/DonorInventory.css";

const AllocationTrendChart = ({ timeline, period, darkMode }) => {
  const { t } = useTranslation();

  // format date label based on period
  const formatDateLabel = (dateStr) => {
    if (!dateStr) return "";

    try {
      const date = new Date(dateStr);

      switch (period) {
        case "W":
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });
        case "M":
          return date.toLocaleDateString("en-US", {
            month: "short",
            year: "2-digit",
            timeZone: "UTC",
          });
        case "Y":
          return date.getUTCFullYear().toString();
        default: // Daily
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });
      }
    } catch {
      return dateStr;
    }
  };

  // prepare chart data with formatted dates
  const chartData = useMemo(() => {
    return timeline.map((item) => ({
      ...item,
      displayDate: formatDateLabel(item.date),
    }));
  }, [timeline, period]); // eslint-disable-line react-hooks/exhaustive-deps

  // calculate totals for summary
  const totals = useMemo(() => {
    return timeline.reduce(
      (acc, item) => ({
        quantity: acc.quantity + item.quantity,
        revenue: acc.revenue + item.revenue,
        allocations: acc.allocations + item.allocations,
      }),
      { quantity: 0, revenue: 0, allocations: 0 }
    );
  }, [timeline]);

  // theme colors
  const axisColor = darkMode ? "#aaaaaa" : "#666666";
  const gridColor = darkMode ? "#444444" : "#e0e0e0";
  const quantityColor = "#669d2e";
  const revenueColor = "#2196F3";

  // cursor style
  const cursorStyle = {
    fill: darkMode ? "rgba(102, 157, 46, 0.15)" : "rgba(102, 157, 46, 0.08)",
  };

  // custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}:{" "}
              {entry.name.includes("Revenue") || entry.name.includes("$")
                ? `$${entry.value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}`
                : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // period label for display
  const periodLabel =
    {
      D: t("Daily"),
      W: t("Weekly"),
      M: t("Monthly"),
      Y: t("Yearly"),
    }[period] || t("Daily");

  return (
    <div className="allocation-trend-section">
      {/* Quantity Trend Chart */}
      <div className="chart-card chart-card--full">
        <div className="chart-card-header">
          <div className="chart-card-icon green">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <h5 className="chart-card-title">{t("Allocation Trend")}</h5>
          <span className="chart-card-badge">{periodLabel}</span>
        </div>

        <div className="chart-card-content">
          {chartData.length === 0 ? (
            <div className="chart-empty-state">
              <FontAwesomeIcon icon={faChartLine} className="empty-icon" />
              <p>{t("No timeline data for selected period")}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="displayDate"
                  stroke={axisColor}
                  fontSize={11}
                  tickMargin={8}
                />
                <YAxis
                  yAxisId="left"
                  stroke={quantityColor}
                  fontSize={11}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={revenueColor}
                  fontSize={11}
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="quantity"
                  stroke={quantityColor}
                  strokeWidth={3}
                  name={t("Quantity (lbs)")}
                  dot={{ fill: quantityColor, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: "#4a7a22" }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revenue"
                  stroke={revenueColor}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name={t("Revenue ($)")}
                  dot={{ fill: revenueColor, strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, fill: "#1565C0" }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Allocations per Period Bar Chart */}
      <div className="chart-card chart-card--full">
        <div className="chart-card-header">
          <div className="chart-card-icon amber">
            <FontAwesomeIcon icon={faBoxOpen} />
          </div>
          <h5 className="chart-card-title">{t("Allocations per Period")}</h5>
          <span className="chart-card-badge">
            {totals.allocations} {t("total")}
          </span>
        </div>

        <div className="chart-card-content">
          {chartData.length === 0 ? (
            <div className="chart-empty-state">
              <p>{t("No allocation data for selected period")}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={gridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="displayDate"
                  stroke={axisColor}
                  fontSize={11}
                  tickMargin={8}
                />
                <YAxis stroke={axisColor} fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
                <Bar
                  dataKey="allocations"
                  fill={quantityColor}
                  name={t("Allocations")}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Period Summary */}
      {chartData.length > 0 && (
        <div className="trend-summary">
          <div className="trend-summary-card">
            <FontAwesomeIcon icon={faBoxOpen} className="summary-icon green" />
            <div className="summary-content">
              <span className="summary-value">
                {totals.quantity.toLocaleString()} lbs
              </span>
              <span className="summary-label">{t("Total Quantity")}</span>
            </div>
          </div>
          <div className="trend-summary-card">
            <FontAwesomeIcon
              icon={faDollarSign}
              className="summary-icon blue"
            />
            <div className="summary-content">
              <span className="summary-value">
                $
                {totals.revenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
              <span className="summary-label">{t("Total Revenue")}</span>
            </div>
          </div>
          <div className="trend-summary-card">
            <FontAwesomeIcon
              icon={faChartLine}
              className="summary-icon amber"
            />
            <div className="summary-content">
              <span className="summary-value">{totals.allocations}</span>
              <span className="summary-label">{t("Total Allocations")}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AllocationTrendChart.propTypes = {
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      revenue: PropTypes.number.isRequired,
      allocations: PropTypes.number.isRequired,
    })
  ).isRequired,
  period: PropTypes.oneOf(["D", "W", "M", "Y"]),
  darkMode: PropTypes.bool,
};

AllocationTrendChart.defaultProps = {
  period: "D",
};

export default AllocationTrendChart;
